import { LocalDateFieldModel, TimeFieldModel } from "@/common/commonForms";
import { EventDispatcher } from "@/events/eventsDomain";
import {
  DCChangeEventHandler,
  useChangeEventHandler,
  useDispatch,
  useFocusEventHandler,
  useKeyboardEventHandler,
  useTranslation,
} from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { parseDateInput, parseTimeInput, toDate, toLocalDateISOString } from "@/utils/Dates";
import { when } from "@/utils/functions";
import { isEnter } from "@/utils/keyboardEvents";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { observer } from "mobx-react";
import { useContext, useRef } from "react";
import { IconButton } from "./Button";
import { DatePicker } from "./DatePicker";
import { TableContext } from "./TableContext";
import { TextInput, TextInputProps } from "./TextInput";

export type DateInputState = { parsed: Date | null; input: string; error: boolean };

export type DateInputProps = Omit<TextInputProps, "type" | "onChange"> & {
  onInput: DCChangeEventHandler<HTMLInputElement>;
  onChange: (params: DateInputState, dispatch: EventDispatcher) => void;
  minDate?: string;
  maxDate?: string;
};

export function DateInput({ placeholder, onChange, minDate, maxDate, ...props }: DateInputProps) {
  const { t } = useTranslation();
  const tableContext = useContext(TableContext);
  const dispatch = useDispatch();
  const blurHandler = useFocusEventHandler(props.onBlur);
  const inputHandler = useChangeEventHandler(props.onInput);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);
  const actionsRef = useRef<BasePopover.Root.Actions | null>(null);

  return (
    <BasePopover.Root actionsRef={actionsRef}>
      <TextInput
        {...props}
        inputProps={{
          ...props.inputProps,
          className: "min-w-[120px]",
        }}
        type={"text"}
        placeholder={placeholder ?? t("date-picker.placeholder")}
        onKeyDown={(event) => {
          if (isEnter(event)) {
            onChange(parseDateInput(event.currentTarget.value), dispatch);
          } else {
            keyDownHandler?.(event);
          }
        }}
        onChange={inputHandler}
        onBlur={(event) => {
          onChange(parseDateInput(String(props.value ?? "")), dispatch);
          if (!event.target.parentElement?.contains(event.relatedTarget)) {
            blurHandler?.(event);
          }
        }}
        message={props.message ?? (props.error ? t("date-picker.invalid-date-message") : undefined)}
        addon={
          props.disabled || props.readOnly ? undefined : (
            <BasePopover.Trigger
              onClick={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              render={
                <IconButton
                  color={"gray"}
                  icon={"calendar"}
                  tooltip={t("show-datepicker")}
                  compact={tableContext?.compact}
                  onBlur={(event) => {
                    if (!open && !event.relatedTarget?.parentElement?.contains(event.target)) {
                      blurHandler?.(event);
                    }
                  }}
                />
              }
            ></BasePopover.Trigger>
          )
        }
      />
      <BasePopover.Portal>
        <BasePopover.Positioner align="end" sideOffset={3}>
          <BasePopover.Popup>
            <DatePicker
              input={String(props.value)}
              minDate={minDate}
              maxDate={maxDate}
              onSelect={(d) => {
                actionsRef.current?.close();
                onChange(
                  { input: toLocalDateISOString(d), parsed: toDate(d), error: false },
                  dispatch,
                );
              }}
            />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

export type MDateInputProps = {
  model: LocalDateFieldModel;
} & Partial<Omit<DateInputProps, "value">>;

export const MDateInput = observer(function MDateInput({
  model,
  onInput,
  onChange,
  onBlur,
  ...props
}: MDateInputProps) {
  const { t } = useTranslation();
  return (
    <DateInput
      {...props}
      minDate={props.minDate ?? model.minDate}
      maxDate={props.maxDate ?? model.maxDate}
      value={model.inputValue}
      onInput={onInput ?? model.setInputValue}
      onChange={(params, dispatch) => {
        if (onChange) {
          onChange(params, dispatch);
        } else {
          model.setAndCommitInputValue(params.input);
        }
      }}
      onBlur={onBlur}
      error={Boolean(model.error)}
      message={when(model.error, t)}
    />
  );
});

export type DateInputViewProps = {
  model: LocalDateFieldModel;
  readonly?: boolean;
};

export const DateInputView = observer(function DateInputView({
  model,
  readonly,
}: DateInputViewProps) {
  const { t } = useTranslation();
  const tableContext = useContext(TableContext);
  const compact = tableContext?.compact;
  const error = Boolean(model.error);
  const actionsRef = useRef<BasePopover.Root.Actions | null>(null);

  return (
    <BasePopover.Root actionsRef={actionsRef}>
      <div
        tabIndex={0}
        className="flex w-full items-center leading-tight relative outline-hidden"
        data-tooltip={model.error ? t(model.error) : undefined}
        data-tooltip-focus
        data-tooltip-color={"error"}
      >
        <div
          className={classNames(
            "flex-1 truncate min-w-[120px]",
            compact ? "p-1" : ["py-3", "pl-4"],
            error && [
              "before:left-0",
              "before:right-0",
              "before:bottom-0",
              "before:absolute",
              "before:border-error",
              "before:border-b-2",
            ],
          )}
        >
          {model.inputValue || <span className="subtle">{t("date-picker.placeholder")}</span>}
        </div>
        {!readonly && (
          <BasePopover.Trigger
            onClick={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            render={
              <IconButton
                color={"gray"}
                icon={"calendar"}
                tooltip={t("show-datepicker")}
                compact={compact}
                tabIndex={-1}
              />
            }
          ></BasePopover.Trigger>
        )}
      </div>
      <BasePopover.Portal>
        <BasePopover.Positioner align="end" sideOffset={3}>
          <BasePopover.Popup>
            <DatePicker
              input={model.inputValue}
              maxDate={model.maxDate}
              onSelect={(d) => {
                actionsRef.current?.close();
                model.setAndCommitInputValue(toLocalDateISOString(d));
              }}
            />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
});

export type TimeInputProps = Omit<TextInputProps, "type" | "onChange"> & {
  onInput: DCChangeEventHandler<HTMLInputElement>;
  onChange: (
    params: { parsed: string | null; input: string; error: boolean },
    dispatch: EventDispatcher,
  ) => void;
};

export function TimeInput({ placeholder, onChange, ...props }: TimeInputProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const blurHandler = useFocusEventHandler(props.onBlur);
  const keyDownHandler = useKeyboardEventHandler(props.onKeyDown);
  const inputHandler = useChangeEventHandler(props.onInput);

  return (
    <TextInput
      {...props}
      type={"text"}
      autoComplete="off"
      placeholder={placeholder ?? t("time-picker.placeholder")}
      onKeyDown={(event) => {
        if (isEnter(event)) {
          onChange(parseTimeInput(event.currentTarget.value), dispatch);
        } else {
          keyDownHandler?.(event);
        }
      }}
      onChange={inputHandler}
      onBlur={(event) => {
        onChange(parseTimeInput(String(props.value ?? "")), dispatch);
        blurHandler?.(event);
      }}
      message={props.message ?? (props.error ? t("date-picker.invalid-time-message") : undefined)}
      icon={"clock"}
    />
  );
}

export type MTimeInputProps = {
  model: TimeFieldModel;
} & Partial<Omit<TimeInputProps, "value">>;

export const MTimeInput = observer(function MTimeInput({
  model,
  label,
  placeholder,
  onInput,
  onChange,
  onBlur,
  ...props
}: MTimeInputProps) {
  const { t } = useTranslation();
  return (
    <TimeInput
      {...props}
      label={label}
      placeholder={placeholder}
      value={model.inputValue}
      onInput={onInput ?? model.setInputValue}
      onChange={onChange ?? model.commit}
      onBlur={onBlur}
      error={Boolean(model.error)}
      message={when(model.error, t)}
    />
  );
});
